import { FC, useState, KeyboardEvent, useRef, FocusEvent, ChangeEvent, useMemo, CSSProperties, } from "react";
// 组件
import { LoadingOutlined } from "@ant-design/icons";
import { ProSpin } from '../antd';
// 方法
import classNames from "classnames";
// 常量
import { ScanInputProps } from "./interface";
import { TABLE_COLUMN_EMPTY, SUCCESS_STATUS_CODE } from '../constants/constant';
import './style.less';

/** 扫码输入框 */
const ScanInput: FC<ScanInputProps> = props => {
  const {
    value, className, style, height, codeName, otherParams, disabled, title, actionRef, loading, recordPrevCode,
    isEmptyData, fetchApi, onBefore, onSuccess, onError, formarParams, onChange, onEnter,
    ...restProps
  } = props;
  const [inputLoading, setInputLoading] = useState<boolean>(false);
  const [prevBarCode, setPrevBarCode] = useState<string>();
  const inputRef = useRef<HTMLInputElement>(null); // 扫码框对象
  /** 获取焦点 */
  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (e.target.value) {
      e.target.select();
    }
  };
  /** 扫描框手动获取焦点 */
  const handleInputFocus = () => {
    inputRef.current?.focus();
  };
  /** change */
  // const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
  //   const newValue = e.target.value;
  //   setBarCode(newValue);
  //   onChange?.(newValue);
  // };
  /** 清除表单内容 */
  const handleClear = (clearValue?: string) => {
    if (!inputRef.current) return;
    if (!clearValue) {
      inputRef.current.value = '';
      return;
    }
    const newValue = inputRef.current?.value;
    if (clearValue && newValue && clearValue === newValue) {
      inputRef.current.value = '';
    }
  };
  /** 选中表单内容 */
  const handleSelect = () => {
    inputRef.current?.select();
  };
  /** 按下回车 */
  const handleKeyUp = async (e: KeyboardEvent<HTMLInputElement>) => {
    // 是否按下回车
    if (e.key !== 'Enter') return;
    const inputElement = e.target as HTMLInputElement;
    const prevValue = inputElement.value.trim();
    handleEnter(prevValue);
  };
  /** 虚拟回车事件 */
  const handleEnter = async (codeValue: string) => {
    // 表单是否有值
    if (!codeValue) return;
    handleSelect();
    if (inputLoading) return;
    recordPrevCode && setPrevBarCode(codeValue);
    // 自定义回车事件
    if (onEnter) {
      onEnter(codeValue);
      return;
    }
    // 条码接口是否存在
    if (!fetchApi) {
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    /** 获取接口之前回调 */
    if (onBefore) {
      const beforeResult = await onBefore(codeValue);
      if (beforeResult === false) return;
    }
    // 接口参数
    let params: any = {...otherParams};
    if (codeName) params[codeName] = codeValue;
    /** 接口参数处理 */
    if (formarParams) {
      params = formarParams(params);
      if (params === false) return;
    }
    setInputLoading(true);
    const res = await fetchApi(params);
    setInputLoading(false)
    if (res.code === SUCCESS_STATUS_CODE) {
      const data = res.data;
      if (data || isEmptyData) {
        if (inputRef.current) {
          const newInputValue = inputRef.current.value;
          if (codeName && newInputValue === params[codeName]) inputRef.current.value = '';
        }
      }
      onSuccess?.(codeValue, data);
    } else {
      onError?.(res);
    }
  };
  /** 失去焦点 */
  const handleBlur = () => {
    inputRef.current?.blur();
  };
  const classes = useMemo(() => {
    return classNames('yb-scan-input', className, {
      'yb-scan-input-lg': !!title,
    })
  }, [className, title]);
  const wrapperStyle: CSSProperties = useMemo(() => {
    let newStyle: CSSProperties = { ...style };
    if (height && height > 30) {
      newStyle.height = `${height}px`;
      newStyle.lineHeight = `${height - 22}px`;
    }
    return newStyle;
  }, [style, height]);
  if (actionRef) {
    actionRef.current = {
      onEnter: handleEnter,
      onClear: handleClear,
      onSelect: handleSelect,
      focus: handleInputFocus,
      blur: handleBlur,
    };
  }
  return (
    <div className={classes} style={wrapperStyle}>
      {!!title && <div className="yb-scan-input-title">{title}</div>}
      <input
        autoComplete="off"
        className="yb-scan-input-form"
        ref={inputRef}
        disabled={disabled}
        onKeyUp={handleKeyUp}
        onFocus={handleFocus}
        {...restProps}
      />
      {
        (loading || inputLoading) && (
          <ProSpin
            className="yb-scan-input-loading"
            spinning={loading || inputLoading}
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          />
        )
      }
      {
        recordPrevCode && (
          <div className="yb-scan-input-prev">
            <span className="input-prev-label">上次扫码:</span>
            <span className="input-prev-text">{prevBarCode || TABLE_COLUMN_EMPTY}</span>
          </div>
        )
      }
    </div>
  );
};

ScanInput.defaultProps = {
  codeName: 'barcode',
  disabled: false,
  recordPrevCode: true,
  isEmptyData: false,
  loading: false,
};

export default ScanInput;
